<script lang="ts">
    import { onMount, onDestroy } from "svelte";
    import { createEventDispatcher } from "svelte";
    import { Editor } from "@tiptap/core";
    import { editorSuggestion } from "./utils";
    import { Plugin } from "prosemirror-state";
    import Document from "@tiptap/extension-document";
    import Paragraph from "@tiptap/extension-paragraph";
    import Text from "@tiptap/extension-text";
    import Bold from "@tiptap/extension-bold";
    import Italic from "@tiptap/extension-italic";
    import Strike from "@tiptap/extension-strike";
    import Underline from "@tiptap/extension-underline";
    import Image from "@tiptap/extension-image";
    import Placeholder from "@tiptap/extension-placeholder";
    import OrderedList from "@tiptap/extension-ordered-list";
    import BulletList from "@tiptap/extension-bullet-list";
    import ListItem from "@tiptap/extension-list-item";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { TenantStore } from "$/stores/TenantStore";
    import TagSuggestionList from "./extensions/tag/TagSuggestionList.svelte";
    import { Tag } from "./extensions/tag/tags";
    import MentionSuggestionList from "./extensions/mention/MentionSuggestionList.svelte";
    import { Mention } from "./extensions/mention/mentions";
    import { Link } from "./extensions/link/link";
    import RolePicker from "./extensions/mention/RolePicker.svelte";
    import AddKeyboardShortcuts from "./extensions/addKeyboardShortcuts/addKeyboardShortcuts";
    import { getEffectivePricingPlan, isEmail } from "$/utils/Utils";
    import { CommentMentions } from "$/utils/CommentMentions";
    import { isMobile } from "$/utils/isMobile";
    import { convertImageToData64 } from "./plugins/convertImageToData64";

    export let editorInputValue = "";
    export let content: string = "";
    export let userRole = TopicAccessLevel.OWNER;
    export let action: () => Promise<void> = async () => {};
    export let parentWrapper = null;
    export let placeholder = "";
    export let mentions = new CommentMentions();
    export let autoFocus = true;

    const dispatch = createEventDispatcher();

    let element;
    let editor;
    let tagItems = [];
    let initMentionList = true;
    let isInbox = ActiveTopicStore?.isInbox() ?? false;
    let lastActiveTopicId = null;

    $: mentionsWithRole = mentions?.getMentionsWithRole();
    $: currentCollaboratorNumber =
        $TenantStore?.collaborators.length +
        mentionsWithRole.filter(
            (mention) => mention.topicRole.role != TopicAccessLevel.MENTION,
        ).length;
    $: effectivePricingPlan = getEffectivePricingPlan($TenantStore?.active);
    $: maxCollabCountReached =
        currentCollaboratorNumber > 4 && effectivePricingPlan === 0;
    $: if ($ActiveTopic?.tags) {
        tagItems = $ActiveTopic.tags;
    }

    export const setReplyMention = (initialMentionJson) => {
        const doc = {
            type: "doc",
            content: [
                {
                    type: "paragraph",
                    content: [
                        {
                            type: "mention",
                            attrs: initialMentionJson,
                        },
                        {
                            // Add space after mention
                            type: "text",
                            text: " ",
                        },
                    ],
                },
            ],
        };
        initMentionList = false;
        editor.commands.setContent(doc);
    };

    onMount(() => {
        mentions.clear();
        editor = new Editor({
            autofocus: autoFocus ? "end" : false,
            content: content,
            element: element,
            extensions: [
                Document,
                Paragraph,
                Text,
                OrderedList,
                BulletList,
                ListItem,
                Bold,
                Italic,
                Strike,
                AddKeyboardShortcuts.configure({
                    enterHandler: async (editor) => {
                        const isInList = editor.isActive("listItem");

                        // if the editor is not active in tag, mention, nor list or if it's on mobile skip submitting
                        if (
                            !editor.isActive("tag") &&
                            !editor.isActive("mention") &&
                            !isInList &&
                            !$isMobile
                        ) {
                            await action();

                            // after action we have to clean the editor content, else on next action the content will be duplicated
                            // which was the previous hack to avoid rendering editor on enter key press after editor is destroyed.
                            editor.commands.clearContent();

                            // HACK: Investigate why content is not clearing with the line above. Await on action is needed for
                            //       highlighter to work properly (content was cleared and is needed for action to complete)
                            //       Without this line there's a bug that line above should prevent
                            editorInputValue = "";
                        }
                    },
                    // if the editor is not active in tag or mention on shift+enter split the block
                    // so user can continue typing in new block that is able to create new UL/OL/Code etc...
                    shiftEnterHandler: (editor) => {
                        if (
                            !editor.isActive("tag") &&
                            !editor.isActive("mention")
                        ) {
                            editor.commands.first(({ commands }) => [
                                () => commands.newlineInCode(),
                                () => commands.splitListItem("listItem"),
                                () => commands.createParagraphNear(),
                                () => commands.liftEmptyBlock(),
                                () => commands.splitBlock(),
                            ]);
                        }
                    },
                }),
                Document.extend({
                    addKeyboardShortcuts() {
                        return {
                            "Mod-v": (view) => {
                                return false;
                            },
                            "Tab": ({ editor }) => {
                                // Only dispatch if not in tag/mention mode
                                if (!editor.isActive("tag") && !editor.isActive("mention")) {
                                    dispatch('tabPressed');
                                    return true; // Prevent default tab behavior
                                }
                                return false; // Allow default tab behavior in tag/mention contexts
                            },
                        };
                    },
                    addProseMirrorPlugins() {
                        return [
                            new Plugin({
                                props: {
                                    handlePaste(view, event) {
                                        if (view.state.mention$?.active) {
                                            const plainText =
                                                event.clipboardData
                                                    ?.getData("text/plain")
                                                    .trim();
                                            if (isEmail(plainText.slice(1))) {
                                                handlePaste(event);
                                                return true;
                                            }
                                        }
                                        return false;
                                    },
                                },
                            }),
                            convertImageToData64(),
                        ] as Plugin<any>[];
                    },
                }),
                Placeholder.configure({
                    placeholder: placeholder,
                    showOnlyWhenEditable: true,
                    showOnlyCurrent: true,
                }),
                Tag.configure({
                    suggestion: editorSuggestion(
                        (query) => filterTags(tagItems, query),
                        TagSuggestionList,
                        userRole,
                        parentWrapper,
                    ),
                }),
                Image.configure({
                    allowBase64: true,
                    inline: true,
                }),
                Link.configure({
                    autolink: false,

                    HTMLAttributes: {
                        class: "text-pink-500",
                    },
                }),
                Mention.configure({
                    suggestion: editorSuggestion(
                        searchMentions,
                        MentionSuggestionList,
                        userRole,
                        parentWrapper,
                        mentions,
                    ),
                }),
                Underline,
            ],
            onCreate: ({ editor }) => {
                //Set initial mentions when editing a comment, but skip it if replying to one
                if (initMentionList) {
                    mentions.setInitialMentions(editor.getJSON());
                }
            },
            onTransaction: ({ editor }) => {
                // force re-render so `editor.isActive` works as expected
                editor = editor;
                editorInputValue = editor.getHTML();
            },
            onUpdate({ editor }) {
                mentions = mentions.rebuildMentionsList(editor.getJSON());
            },
        });
    });

    function filterTags(tags, query) {
        if (!query) return tags;
        return tags.filter((tag) =>
            tag.tag.toLowerCase().startsWith(query.toLowerCase()),
        );
    }

    function searchMentions(query: string) {
        return ActiveTopicStore.isInbox()
            ? TenantStore.searchCollaborators(query)
            : ActiveTopicStore.searchCollaborators(query);
    }

    function handlePaste(event: ClipboardEvent): void {
        const plainText =
            event.clipboardData?.getData("text/plain").trim() || "";
        const sanitizedText = plainText
            .replace(/<a [^>]*href="([^"]*)"[^>]*>[^<]*<\/a>/g, "$1")
            .trim();
        editor.commands.insertContent(`${sanitizedText}`);
    }

    let activeTopicSubscription = ActiveTopic.subscribe(() => {
        if ($ActiveTopic && $ActiveTopic?.id !== lastActiveTopicId) {
            lastActiveTopicId = $ActiveTopic.id;
            isInbox = ActiveTopicStore.isInbox();
            if (isInbox && editor) {
                mentions.changeAllRolesToMentionOnly();
                mentions = mentions.rebuildMentionsList(editor.getJSON());
            }
        }
    });

    // NOTE: Do we need to destroy the editor on unmount?
    onDestroy(() => {
        if (editor) {
            editor.destroy();
        }
        activeTopicSubscription();
    });
</script>

<div>
    <div class="editor" bind:this={element}/>
    {#if mentionsWithRole.length && userRole >= TopicAccessLevel.EDITOR}
        <div class="collaborator-share-title">Collaborators</div>
        {#each mentionsWithRole as mention}
            {#if maxCollabCountReached && !TenantStore.isCollaborator(mention.user.username) && mention.topicRole.role === TopicAccessLevel.MENTION}
                <div class="collaborator-share-user">
                    <RolePicker
                        bind:mention
                        mentionOnly={false}
                        disabled={true}
                        showUpgradeCTA={true}
                    />
                </div>
            {:else}
                <div class="collaborator-share-user">
                    <RolePicker bind:mention mentionOnly={isInbox} />
                </div>
            {/if}
        {/each}
    {/if}
</div>

<style>
    @import "../../main.css";

    /* purgecss start ignore */
    :global(.ProseMirror),
    :global(.tiptap) {
        @apply whitespace-pre-wrap focus-visible:outline-none !important;
        word-break: break-word;
        overflow-wrap: break-word;
    }

    :global(.tiptap .suggestion),
    :global(.tiptap cw-mention) {
        @apply editor-suggestion !important;
    }

    :global(.tiptap cw-tag) {
        @apply editor-suggestion border-sky-200 bg-sky-100 !important;
        word-break: break-word;
        overflow-wrap: anywhere;
    }

    :global(.tiptap cw-tag span) {
        @apply inline leading-none text-sky-600 !important;
        word-break: break-word;
        overflow-wrap: anywhere;
        vertical-align: baseline;
    }

    :global(.tiptap p) {
        @apply reset block text-sm !important;
    }

    :global(.tiptap span) {
        @apply reset inline text-sm !important;
    }

    :global(.tiptap img) {
        @apply reset h-auto max-w-full rounded-lg !important;
    }

    :global(.tiptap a) {
        @apply reset inline text-sm text-pink-500 !important;
    }

    :global(.tiptap a strong),
    :global(.tiptap a b) {
        @apply inline font-bold !important;
    }

    :global(.tiptap a > *) {
        @apply reset text-sm text-pink-500 !important;
    }

    :global(.tiptap ul) {
        @apply reset block list-inside list-disc !important;
    }

    :global(.tiptap ol) {
        @apply reset block list-inside list-decimal !important;
    }

    :global(.tiptap li::marker) {
        @apply reset block text-sm !important;
    }

    :global(.tiptap li p) {
        @apply reset inline text-sm !important;
    }

    /* Placeholder - https://tiptap.dev/docs/editor/extensions/functionality/placeholder#additional-setup */
    :global(.tiptap p.is-editor-empty:first-child::before) {
        color: #adb5bd;
        content: attr(data-placeholder);
        float: left;
        height: 0;
        pointer-events: none;
    }

    /* purgecss end ignore */

    .collaborator-share-title {
        @apply border-t border-gray-200 bg-gray-100 px-4 py-1 text-sm text-gray-400;
    }

    .editor {
        @apply cursor-text px-4 py-2;
    }

    .collaborator-share-user {
        @apply flex flex-row justify-between px-4 py-2 text-sm;
    }
</style>
